import Form, { useForm } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Modal from 'antd/lib/modal/Modal';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { tryUpdateRelease, UpdateReleasePlanningSummaryRequest } from '../actions/release';
import { ReleaseUpdatable } from '../actions/updatable';
import { getError, getIsLoading, getUpdated } from '../helpers';
import { RootState } from '../reducers/reducers';
import { ReleaseComponentProps } from '../typescriptHelpers';
import { globalMessage } from '../utils/globalMessage';
import MarkdownEditor from './HOCs/MarkdownEditor';
import { GenerateReleaseNotesDropdown } from './sharedComponents/GenerateReleaseNotesDropdown';
import Space from 'antd/lib/space';
import { AllPatternTokens, GeneratedReleaseNotesMarkdownContext, GeneratedReleaseNotesStepContext } from '@/types';
import { PatternTokenHelperTextComponent } from './appSettings/PatternTokenHelperTextComponent';
import Popover from 'antd/lib/popover';
import { InfoCircleOutlined } from '@ant-design/icons';
import { antGrey } from './styles/globalStyles';

interface PlanningSummaryModalProps extends ReleaseComponentProps {
    open: boolean;
    displayableVersion: string;
    versionSummary?: string;
    onCancel: () => void;
    onDidUpdate: () => void;
}

const PlanningSummaryModal: React.FC<PlanningSummaryModalProps> = (props: PlanningSummaryModalProps) => {
    const [form] = useForm();
    const dispatch = useDispatch();

    const updatePlanningSummaryLoading = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.UpdatePlanningSummary, state),
    );
    const updatePlanningSummarySuccess = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.UpdatePlanningSummary, state),
    );

    const updatePlanningSummaryError = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.UpdatePlanningSummary, state),
    );

    const saveChanges = () => {
        form.validateFields().then((values) => {
            const summary = values.versionSummary;

            const request: UpdateReleasePlanningSummaryRequest = {
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                releasePlanningSummary: summary,
            };

            dispatch(tryUpdateRelease(ReleaseUpdatable.UpdatePlanningSummary, request));
        });
    };

    React.useEffect(() => {
        form.setFieldsValue({
            versionSummary: props.versionSummary ?? '',
        });
    }, [props.versionSummary]);

    React.useEffect(() => {
        if (updatePlanningSummarySuccess) {
            props.onDidUpdate();

            globalMessage.success('Release description updated!');
        }
    }, [updatePlanningSummarySuccess]);

    React.useEffect(() => {
        if (updatePlanningSummaryError) {
            globalMessage.error(updatePlanningSummaryError);
        }
    }, [updatePlanningSummaryError]);

    return (
        <Modal
            open={props.open}
            title={`Edit ${props.displayableVersion} release description`}
            okText="Save changes"
            confirmLoading={updatePlanningSummaryLoading}
            cancelText="Discard changes"
            onCancel={props.onCancel}
            onOk={() => form.submit()}
            afterClose={() => form.resetFields()}
            width={'700px'}
        >
            <Space direction="vertical" align="end" style={{ display: 'flex', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
                    <GenerateReleaseNotesDropdown
                        orgId={props.orgId}
                        appId={props.appId}
                        releaseId={props.releaseId}
                        stepContext={GeneratedReleaseNotesStepContext.Kickoff}
                        markdownContext={GeneratedReleaseNotesMarkdownContext.MarkdownSupported}
                    />
                </div>
                <Form
                    form={form}
                    initialValues={{
                        versionSummary: props.versionSummary ?? null,
                    }}
                    onFinish={saveChanges}
                >
                    <FormItem
                        name="versionSummary"
                        rules={[{ required: true, message: 'Please enter a release note.' }]}
                        style={{ marginBottom: '0px' }}
                    >
                        <MarkdownEditor autoFocus preview="edit" />
                    </FormItem>
                </Form>
                <Text type="secondary">
                    Pattern token strings are supported. Token strings will be rendered as needed when the Release
                    Description is shared externally in notifications, and when the release completes.{' '}
                    <Popover
                        content={
                            <div style={{ overflow: 'scroll', maxWidth: '40vw', maxHeight: '40vh' }}>
                                <PatternTokenHelperTextComponent availableTokens={AllPatternTokens} />
                            </div>
                        }
                        placement="top"
                    >
                        <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                    </Popover>
                </Text>
            </Space>
        </Modal>
    );
};

export default PlanningSummaryModal;
